import { useContext, useState } from 'react';
import {
  AppInsightsContext,
  UserContext,
  TrackEventKey,
  MetadataContext,
  TrackEventName,
  TrackEventType,
  Modal,
  ProgressCircular,
  Tooltip,
  useNotification,
} from '@/components';
import classNames from 'classnames';

import AIRed from '@/assets/ai-red.svg';
import { saveToScrapbookWithPath, gdAISummary } from '@/api';
import { useRouter } from 'next/router';
import XLight from '@/assets/xLight.svg';
import StylusAIWordmark from '@/assets/stylusAIWordmark_blackRGBRegistered.svg';
import Copy from '@/assets/copy-simple-light.svg';
import ScissorIcon from '@/assets/scissors-light.svg';

type GDAiSummaryButtonModel = {
  className?: string;
  title?: string;
};

const GDAiSummaryButton = ({ className, title }: GDAiSummaryButtonModel) => {
  const appInsights = useContext(AppInsightsContext);
  const user = useContext(UserContext);
  const { pageId } = useContext(MetadataContext);
  const { asPath } = useRouter();
  const { showNotification } = useNotification();

  const [summaryText, setSummaryText] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const openSummary = () => {
    setLoading(true);
    setSummaryText(null);
    setOpen(true);

    appInsights.trackCustomEvent(TrackEventName.AI_PAGE, TrackEventKey.SUMMARY, TrackEventType.CLICK, user, {
      pageId,
      pagePath: asPath,
    });

    gdAISummary(pageId)
      .then((content) => {
        setSummaryText(content.response);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const copySummaryToClipboard = () => {
    if (summaryText) {
      navigator.clipboard.writeText(summaryText);
    }
  };

  const onSaveToScrapbook = async () => {
    if (!summaryText) return;

    setSaving(true);
    try {
      const result = await saveToScrapbookWithPath(
        {
          path: `/Stylus AI/stylus-ai.${Date.now()}.json`,
          contentType: 'application/x.stylus.sytlus-ai-summary+json',
          content: JSON.stringify({
            pageId,
            pageTitle: title,
            response: summaryText,
          }),
        },
        user?.userId,
      );

      if (result.succeeded) {
        showNotification(result.message || 'Saved to Stylus AI', 'savesuccess');
      } else {
        showNotification('Failed to save to scrapbook', 'error');
        console.error(result);
      }
    } catch {
      showNotification('Failed to save to scrapbook', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <button className={classNames(className, 'cursor-pointer bg-black print:hidden')} onClick={openSummary}>
        <AIRed className="ai-icon mx-4 h-6 w-6 shrink-0" />
        <div className="h-[18px] border-r border-white" />
        <div className="chip-content relative px-4 text-white">Generate Summary</div>
      </button>
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        contanerClassName="w-full h-full lg:h-auto lg:max-h-[90%] lg:w-[95%] xl:w-[80%] bg-white !rounded-none lg:!rounded-md disable-scroll"
      >
        <div className="flex h-full w-full flex-col">
          <div className="flex items-start justify-between px-3 py-2 lg:px-6 lg:py-4">
            <div className="text-xl font-medium">
              <StylusAIWordmark className="h-[82px] w-auto" />
            </div>
            <button onClick={() => setOpen(false)} className="btn icon">
              <XLight className="h-7 w-7" />
            </button>
          </div>
          <div data-lenis-prevent className="flex grow-1 flex-col overflow-auto sm:px-4 lg:px-12">
            {loading ? (
              <div className="flex h-[280px] items-center justify-center">
                <ProgressCircular indeterminate size={50} width={4} colorClassName="stroke-sky" />
              </div>
            ) : (
              <div
                dangerouslySetInnerHTML={{ __html: summaryText || '' }}
                className="whitespace-pre-wrap text-black/60"
              />
            )}
          </div>
          {!loading && summaryText && (
            <div className="flex items-start justify-start gap-3 px-3 py-2 lg:px-10 lg:py-4">
              <Tooltip placement="top" content="Copy to Clipboard">
                <button className="btn icon cursor-pointer rounded-full p-2" onClick={copySummaryToClipboard}>
                  <Copy className="h-7 w-7" />
                </button>
              </Tooltip>
              <Tooltip placement="top" content="Save To Scrapbook">
                <button
                  className="btn icon cursor-pointer rounded-full p-2"
                  onClick={onSaveToScrapbook}
                  disabled={saving}
                >
                  {saving ? (
                    <ProgressCircular indeterminate size={28} width={2} colorClassName="stroke-sky" />
                  ) : (
                    <ScissorIcon className="h-7 w-7" />
                  )}
                </button>
              </Tooltip>
            </div>
          )}
        </div>
      </Modal>
    </>
  );
};

export default GDAiSummaryButton;
